import { useEffect, useState } from 'react';
import { Settings, UserCheck, Users, AlertCircle } from 'lucide-react';
import { Button, Spinner } from '@centraoneplus/ui';
import { TeamSupervisorPicker } from './TeamSupervisorPicker';
import { TeamAgentPicker } from './TeamAgentPicker';
import { pipelineService } from '../../../services/pipeline.service';
import type { Pipeline } from '../../../types/pipeline';
import type {
  CreateTeamDto,
  TeamWithMembers,
  UpdateTeamDto,
} from '../../../types/team';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { isParentTeam } from './teamHierarchy';
import { TeamTagInput } from './TeamTagInput';

type EditorTab = 'basic' | 'supervisor' | 'agents';

interface TeamEditorProps {
  team?: TeamWithMembers;
  /**
   * The parent team: when creating, the new team becomes its sub-team; when
   * editing a sub-team, the parent it belongs to.
   */
  parent?: TeamWithMembers;
  onCreate?: (dto: CreateTeamDto) => Promise<void>;
  onUpdate?: (dto: UpdateTeamDto, agentIds: string[]) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  /** Tags already used on other teams, offered as suggestions (not required). */
  knownTags?: string[];
  /**
   * When given (an admin editing an existing team), a "Parent team" dropdown
   * lets the team move under one of these teams or back to the top level.
   * The move itself is confirmed by the caller before it is saved.
   */
  parentOptions?: TeamWithMembers[];
}

interface FormState {
  name: string;
  description: string;
  designation: string;
  tags: string[];
  maxSize: string;
  pipelineId: string;
  supervisorId: string | null;
  agentIds: string[];
  /** '' = top-level team. */
  parentTeamId: string;
}

const initialFormFromTeam = (team?: TeamWithMembers, parent?: TeamWithMembers): FormState => ({
  name: team?.name ?? '',
  description: team?.description ?? '',
  designation: team?.designation ?? '',
  tags: team?.tags ?? [],
  maxSize: team?.maxSize != null ? String(team.maxSize) : '',
  // A sub-team always works its parent's pipeline.
  pipelineId: parent?.pipelineId ?? team?.pipelineId ?? '',
  supervisorId: team?.supervisorId ?? null,
  agentIds: team?.agents?.map((a) => a.id) ?? [],
  parentTeamId: team?.parentTeamId ?? parent?.id ?? '',
});

export function TeamEditor({
  team,
  parent,
  onCreate,
  onUpdate,
  onCancel,
  isSaving,
  knownTags = [],
  parentOptions,
}: TeamEditorProps) {
  const [activeTab, setActiveTab] = useState<EditorTab>('basic');
  const [form, setForm] = useState<FormState>(() => initialFormFromTeam(team, parent));
  // A parent team is a container: a head and sub-teams, no agents of its own.
  const editingParentTeam = !!team && isParentTeam(team);
  // With the dropdown, the parent is whatever is currently chosen in it.
  const selectedParent = parentOptions
    ? parentOptions.find((p) => p.id === form.parentTeamId)
    : parent;
  const isSubTeam = parentOptions ? !!form.parentTeamId : !!parent || !!team?.parentTeamId;
  // Creating the first sub-team under a team that still has agents moves them in.
  const parentAgentsToMove = !team && parent ? parent.agentCount : 0;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loadingPipelines, setLoadingPipelines] = useState(true);
  const [pipelineError, setPipelineError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPipelines(true);
      setPipelineError(null);
      try {
        const list = await pipelineService.getPipelines();
        if (cancelled) return;
        setPipelines(list);
        // Auto-select if exactly one pipeline and editor is empty.
        if (list.length === 1 && !team && !parent && !form.pipelineId) {
          setForm((f) => ({ ...f, pipelineId: list[0].id }));
        }
      } catch (e) {
        if (!cancelled) setPipelineError('Failed to load pipelines');
        console.error(e);
      } finally {
        if (!cancelled) setLoadingPipelines(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  };

  const validate = (): EditorTab | null => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Team name is required';
    else if (form.name.trim().length > 100) next.name = 'Team name is too long (max 100 chars)';
    if (!form.pipelineId) next.pipelineId = 'Pick a pipeline';
    if (form.maxSize) {
      const n = Number(form.maxSize);
      if (!Number.isInteger(n) || n <= 0) next.maxSize = 'Team size must be a positive whole number';
      else if (n > 1000) next.maxSize = 'Team size must be 1000 or less';
      else if (form.agentIds.length > n) {
        next.maxSize = `You have selected ${form.agentIds.length} agents — increase size or remove agents`;
      }
    }
    if (!form.supervisorId) next.supervisorId = 'Select a supervisor';

    setErrors(next);

    if (next.name || next.pipelineId || next.maxSize) return 'basic';
    if (next.supervisorId) return 'supervisor';
    return null;
  };

  const handleSubmit = async () => {
    const errorTab = validate();
    if (errorTab) {
      setActiveTab(errorTab);
      return;
    }
    const maxSizeNum = form.maxSize ? Number(form.maxSize) : undefined;
    if (team && onUpdate) {
      const parentChanged = !!parentOptions && form.parentTeamId !== (team.parentTeamId ?? '');
      const dto: UpdateTeamDto = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        designation: form.designation.trim() || undefined,
        // Always sent, even empty — omitting it would mean "don't touch tags",
        // which would make clearing the last tag impossible.
        tags: form.tags,
        maxSize: maxSizeNum,
        // A sub-team takes its parent's pipeline; the server sets it.
        ...(isSubTeam ? {} : { pipelineId: form.pipelineId }),
        supervisorId: form.supervisorId!,
        ...(parentChanged ? { parentTeamId: form.parentTeamId || null } : {}),
      };
      await onUpdate(dto, form.agentIds);
    } else if (onCreate) {
      const dto: CreateTeamDto = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        designation: form.designation.trim() || undefined,
        tags: form.tags,
        maxSize: maxSizeNum,
        pipelineId: form.pipelineId,
        supervisorId: form.supervisorId!,
        agentIds: form.agentIds,
        ...(parent
          ? { parentTeamId: parent.id, moveParentAgents: parentAgentsToMove > 0 || undefined }
          : {}),
      };
      await onCreate(dto);
    }
  };

  const tabs: { id: EditorTab; label: string; icon: typeof Settings }[] = [
    { id: 'basic', label: 'Basic Info', icon: Settings },
    { id: 'supervisor', label: editingParentTeam ? 'Head' : 'Supervisor', icon: UserCheck },
    // A parent team's agents live in its sub-teams.
    ...(editingParentTeam
      ? []
      : [{ id: 'agents' as const, label: `Agents (${form.agentIds.length})`, icon: Users }]),
  ];

  const tabHasError = (tab: EditorTab): boolean => {
    if (tab === 'basic') return Boolean(errors.name || errors.pipelineId || errors.maxSize);
    if (tab === 'supervisor') return Boolean(errors.supervisorId);
    return false;
  };

  const maxSizeNum = form.maxSize ? Number(form.maxSize) : undefined;

  return (
    <div className="flex h-full flex-col">
      {/* Tabs */}
      <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const hasErr = tabHasError(tab.id);
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {hasErr && <AlertCircle className="h-3.5 w-3.5 text-red-500" />}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'basic' && (
          <div className="space-y-5">
            {parent && !parentOptions && (
              <div
                className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200"
                data-testid="sub-team-notice"
              >
                Sub-team of <strong>{parent.name}</strong>.
                {parentAgentsToMove > 0 && (
                  <>
                    {' '}
                    The {parentAgentsToMove} {parentAgentsToMove === 1 ? 'agent' : 'agents'} currently in{' '}
                    {parent.name} will move into this sub-team, because a team with sub-teams holds
                    no agents of its own. You can move them between sub-teams afterwards.
                  </>
                )}
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Team name *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="e.g., Tier-1 Support"
                maxLength={100}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="What does this team handle?"
                rows={2}
                maxLength={500}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Tags
              </label>
              <TeamTagInput
                value={form.tags}
                onChange={(tags) => setField('tags', tags)}
                knownTags={knownTags}
                placeholder="e.g., Operations, Customer Support..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Designation
                </label>
                <input
                  type="text"
                  value={form.designation}
                  onChange={(e) => setField('designation', e.target.value)}
                  placeholder="e.g., Customer Success"
                  maxLength={100}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Team size
                </label>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={form.maxSize}
                  onChange={(e) => setField('maxSize', e.target.value)}
                  placeholder="Optional capacity"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
                {errors.maxSize && (
                  <p className="mt-1 text-xs text-red-500">{errors.maxSize}</p>
                )}
              </div>
            </div>

            {parentOptions && team && (
              <div>
                <label
                  htmlFor="team-parent-picker"
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Parent team
                </label>
                <ThemedSelect
                  id="team-parent-picker"
                  value={form.parentTeamId}
                  onChange={(e) => {
                    const nextParent = parentOptions.find((p) => p.id === e.target.value);
                    setField('parentTeamId', e.target.value);
                    // Shown straight away; a sub-team works its parent's pipeline.
                    setField('pipelineId', nextParent?.pipelineId ?? team.pipelineId);
                  }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="">None (top-level team)</option>
                  {parentOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </ThemedSelect>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  You'll see what the move changes before it is saved.
                </p>
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Pipeline *
              </label>
              {loadingPipelines ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Loading pipelines...</div>
              ) : pipelineError ? (
                <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="h-4 w-4" />
                  {pipelineError}
                </div>
              ) : pipelines.length === 0 ? (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
                  No pipelines exist. Create one in <strong>Settings → Pipelines</strong> first.
                </div>
              ) : (
                <ThemedSelect
                  value={form.pipelineId}
                  onChange={(e) => setField('pipelineId', e.target.value)}
                  disabled={isSubTeam}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="">Select a pipeline</option>
                  {pipelines.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </ThemedSelect>
              )}
              {errors.pipelineId && (
                <p className="mt-1 text-xs text-red-500">{errors.pipelineId}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isSubTeam
                  ? `A sub-team always uses its parent team's pipeline${selectedParent ? ` (${selectedParent.name})` : ''}.`
                  : editingParentTeam
                    ? 'Changing this also changes the pipeline of every sub-team.'
                    : 'Tickets in this pipeline can be auto-routed to this team.'}
              </p>
            </div>
          </div>
        )}

        {activeTab === 'supervisor' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {editingParentTeam
                ? 'The head leads this team, sees every sub-team\'s tickets and moves tickets from its queue into a sub-team.'
                : 'The supervisor leads this team and can monitor agent performance.'}
            </p>
            <TeamSupervisorPicker
              value={form.supervisorId}
              onChange={(id) => setField('supervisorId', id)}
              disabled={isSaving}
            />
            {errors.supervisorId && (
              <p className="text-xs text-red-500">{errors.supervisorId}</p>
            )}
          </div>
        )}

        {activeTab === 'agents' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Pick the agents who will work under this supervisor.
            </p>
            <TeamAgentPicker
              value={form.agentIds}
              onChange={(ids) => setField('agentIds', ids)}
              maxSize={maxSizeNum}
              disabled={isSaving}
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
        <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? <Spinner size="sm" /> : team ? 'Save Changes' : 'Create Team'}
        </Button>
      </div>
    </div>
  );
}
